import { useState, useRef, useCallback } from "react";
import { Upload, ImagePlus, X } from "lucide-react";

export function ImageDropZone({
  onFileSelect,
  preview,
  fileName,
  accept = "image/*",
  compact = false,
  testId = "image-drop-zone",
  label,
  className = "",
  onClear,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const dragCounter = useRef(0);

  const handleFile = useCallback(
    (file) => {
      if (!file) return;
      const acceptTypes = accept.split(",").map((t) => t.trim());
      const valid = acceptTypes.some((t) => {
        if (t.endsWith("/*")) return file.type.startsWith(t.replace("/*", "/"));
        return file.type === t || file.name.endsWith(t);
      });
      if (!valid) return;
      onFileSelect(file);
    },
    [accept, onFileSelect]
  );

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const onClick = () => inputRef.current?.click();

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
    e.target.value = "";
  };

  if (compact) {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <div
          className={`w-20 h-20 rounded-sm border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-all duration-200 ${
            isDragging
              ? "border-primary bg-primary/10 scale-105"
              : preview
              ? "border-border hover:border-primary/50"
              : "border-border/60 bg-muted/20 hover:border-primary/40 hover:bg-muted/30"
          }`}
          onClick={onClick}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          data-testid={testId}
        >
          {preview ? (
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-cover"
              crossOrigin="anonymous"
            />
          ) : isDragging ? (
            <ImagePlus className="w-6 h-6 text-primary animate-pulse" />
          ) : (
            <Upload className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          <p>{isDragging ? "Drop image here" : `Drag & drop or click to ${preview ? "change" : "add"}`}</p>
          {fileName && <p className="text-primary mt-0.5 truncate max-w-[140px]">{fileName}</p>}
          {preview && onClear && (
            <button
              type="button"
              className="text-destructive/70 hover:text-destructive mt-0.5 flex items-center gap-0.5"
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              data-testid={`${testId}-clear`}
            >
              <X className="w-3 h-3" /> Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={onInputChange}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-sm border-2 border-dashed cursor-pointer transition-all duration-200 ${
        isDragging
          ? "border-primary bg-primary/10"
          : preview
          ? "border-border hover:border-primary/40"
          : "border-border/60 bg-muted/10 hover:border-primary/40 hover:bg-muted/20"
      } ${className}`}
      onClick={onClick}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={testId}
    >
      {preview ? (
        <div className="relative group">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-40 object-cover rounded-sm"
            crossOrigin="anonymous"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 rounded-sm">
            <span className="text-xs text-white/90 font-medium">
              Drop or click to replace
            </span>
            {onClear && (
              <button
                type="button"
                className="text-xs text-destructive bg-background/80 px-2 py-1 rounded-sm hover:bg-background"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                data-testid={`${testId}-clear`}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 px-4">
          {isDragging ? (
            <ImagePlus className="w-8 h-8 text-primary animate-pulse mb-2" />
          ) : (
            <Upload className="w-8 h-8 text-muted-foreground mb-2" />
          )}
          <p className="text-sm text-muted-foreground text-center">
            {isDragging ? "Drop image here" : label || "Drag & drop an image, or click to browse"}
          </p>
          {fileName && (
            <p className="text-xs text-primary mt-1 truncate max-w-full">
              {fileName}
            </p>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={onInputChange}
      />
    </div>
  );
}
